/**
 * Searchable employee picker — a small combobox for admin pages.
 *
 * Type part of a name, email or job ID to filter (case- and accent-insensitive,
 * so "tomas" finds "Tomás"). Arrow keys move, Enter picks, Escape closes.
 * With `allowAll`, an "All employees" entry (value `ALL_EMPLOYEES`) is offered
 * while the search box is empty.
 */

import { useId, useMemo, useRef, useState, type CSSProperties } from "react";

import type { Member } from "@/lib/types";

export const ALL_EMPLOYEES = "all";

type Props = {
  members: Member[];
  /** Selected user_id, `ALL_EMPLOYEES`, or null when nothing is chosen yet. */
  value: string | null;
  onChange: (value: string) => void;
  allowAll?: boolean;
  placeholder?: string;
  ariaLabel?: string;
};

type Option = { value: string; label: string; hint: string };

function norm(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function EmployeePicker({
  members,
  value,
  onChange,
  allowAll = false,
  placeholder = "Search employees",
  ariaLabel = "Employee",
}: Props) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const selected = members.find((m) => m.user_id === value);
  const selectedLabel =
    value === ALL_EMPLOYEES
      ? "All employees"
      : selected
        ? `${selected.display_name}${selected.job_id ? ` (${selected.job_id})` : ""}`
        : "";

  const options = useMemo<Option[]>(() => {
    const q = norm(query.trim());
    const matched = members
      .filter((m) => !q || norm(`${m.display_name} ${m.email} ${m.job_id ?? ""}`).includes(q))
      .sort((a, b) => a.display_name.localeCompare(b.display_name))
      .map((m) => ({
        value: m.user_id,
        label: m.display_name,
        hint: [m.job_id, m.email].filter(Boolean).join(" · "),
      }));
    return allowAll && !q
      ? [{ value: ALL_EMPLOYEES, label: "All employees", hint: "" }, ...matched]
      : matched;
  }, [members, query, allowAll]);

  const choose = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const optionId = (i: number) => `${listId}-opt-${i}`;

  return (
    <div style={{ position: "relative", width: 260 }}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && options[active] ? optionId(active) : undefined}
        value={open ? query : selectedLabel}
        placeholder={selectedLabel || placeholder}
        onFocus={() => {
          setOpen(true);
          setQuery("");
          setActive(0);
        }}
        onBlur={() => setOpen(false)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(i + 1, Math.max(options.length - 1, 0)));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (open && options[active]) choose(options[active].value);
          } else if (e.key === "Escape") {
            setOpen(false);
            setQuery("");
            e.currentTarget.blur();
          }
        }}
        style={inputStyle}
      />
      {open && (
        <ul id={listId} role="listbox" aria-label={ariaLabel} style={listStyle}>
          {options.length === 0 ? (
            <li style={{ ...optionStyle, color: "var(--mute)", cursor: "default" }}>
              No matching employees
            </li>
          ) : (
            options.map((o, i) => (
              <li
                key={o.value}
                id={optionId(i)}
                role="option"
                aria-selected={o.value === value}
                // mousedown, not click: keeps the input from blurring (and the
                // list from closing) before the choice registers.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o.value);
                }}
                onMouseEnter={() => setActive(i)}
                style={{
                  ...optionStyle,
                  background: i === active ? "var(--surface-2)" : "transparent",
                }}
              >
                <span style={{ fontWeight: o.value === value ? 600 : 400 }}>{o.label}</span>
                {o.hint && <span style={hintStyle}>{o.hint}</span>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

const inputStyle: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "6px 10px",
  fontFamily: "var(--font-sans)",
  fontSize: 13,
  color: "var(--ink)",
  outline: "none",
};

const listStyle: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  zIndex: 20,
  maxHeight: 280,
  overflowY: "auto",
  margin: 0,
  padding: 4,
  listStyle: "none",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  boxShadow: "var(--shadow-3)",
};

const optionStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "6px 8px",
  borderRadius: 4,
  fontSize: 13,
  color: "var(--ink)",
  cursor: "pointer",
};

const hintStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--mute)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
